/**
 * usePrompt hook to block navigation with a prompt
 *
 * Note: There is an unavoidable edge case that triggers a warning from the history library:
 * "You are trying to block a pop navigation to a location that was not created by the history library."
 *
 * This occurs when navigation originates outside the app (e.g. browser address bar, pasted URL).
 * In these cases, the history blocker cannot intercept the transition.
 *
 * This does not affect blocking of in-app navigations created via the history API.
 * See: https://github.com/remix-run/history/issues/872
 */

import { useBlocker } from "@platform/navigation-control/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

const usePrompt = (
  when = false,
  defaultLocation,
  state,
  exceptions = [],
  defaultPrevPath,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showPrompt, setShowPrompt] = useState(false);
  const [lastLocation, setLastLocation] = useState(defaultLocation);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const popstateBlockedRef = useRef(false);

  const cancelNavigation = useCallback(() => {
    popstateBlockedRef.current = false;
    setShowPrompt(false);
    setLastLocation(null);
    setConfirmedNavigation(false);
  }, []);

  const confirmNavigation = useCallback(() => {
    popstateBlockedRef.current = false;
    setShowPrompt(false);
    setConfirmedNavigation(true);
  }, []);

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (
        nextLocation.action !== "REPLACE" &&
        exceptions.includes(nextLocation.location.pathname)
      ) {
        setConfirmedNavigation(true);
        setLastLocation(nextLocation);
        return;
      }

      if (nextLocation.location.pathname !== location.pathname && !showPrompt) {
        setShowPrompt(true);
        setLastLocation(nextLocation);
      }
    },
    [exceptions, location.pathname, showPrompt],
  );

  // to handle in-app navigation blocking
  useEffect(() => {
    if (!confirmedNavigation || !lastLocation) return;

    const path = lastLocation.location?.pathname || defaultPrevPath;

    if (lastLocation.action === "REPLACE") {
      if (path === "/") navigate(0);
      else navigate(path, { state, replace: true });
    } else {
      navigate(path, { state });
    }

    setConfirmedNavigation(false);
  }, [confirmedNavigation, lastLocation, defaultPrevPath, navigate, state]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!when) return;
      event.preventDefault();
      event.returnValue = "Are you sure you want to leave?";
    };

    const handlePopState = () => {
      if (!when || popstateBlockedRef.current) return;

      popstateBlockedRef.current = true;
      setShowPrompt(true);

      setLastLocation({
        location: { pathname: window.location.pathname },
        action: "POP",
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [when]);

  useBlocker(handleBlockedNavigation, when);

  return [showPrompt, confirmNavigation, cancelNavigation];
};

export default usePrompt;
